import { css } from '@emotion/react';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';

const containerStyle = css`
  border-radius: ${theme.borderRadius.small};
  border: 1px solid ${theme.colors.primary10};
  padding: ${theme.spacing(1, 1.5)};
  max-width: 320px;
  margin-bottom: ${theme.spacing(1)};
`;

interface ContainerStyleConfig {
  hasError: boolean;
  isEnhancedFlowEnabled: boolean;
}

export function getContainerStyle({ hasError, isEnhancedFlowEnabled }: ContainerStyleConfig) {
  const variants = [containerStyle];

  if (isEnhancedFlowEnabled) {
    variants.push(css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      max-width: none;
      border: 0;
      border-bottom: 1px solid var(--fabric-color-neutral-20);
      margin-bottom: 0;

      @media screen and (max-width: ${breakpoints.small.max}px) {
        align-items: stretch;
        flex-direction: column;
        justify-content: flex-start;
        padding: var(--fabric-space-gap-md);
      }

      &:last-of-type {
        border-bottom: none;
      }
    `);
  }

  if (hasError) {
    variants.push(css`
      border-color: var(--fabric-color-critical-20);
      border-bottom-color: var(--fabric-color-critical-20);
    `);
  }

  return variants;
}

const innerContainerStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};

  .button--container {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  }
`;

interface InnerContainerStyleConfig {
  isEnhancedFlowEnabled: boolean;
}

export function getInnerContainerStyle({ isEnhancedFlowEnabled }: InnerContainerStyleConfig) {
  if (isEnhancedFlowEnabled) {
    return [
      innerContainerStyle,
      css`
        flex: 1;
        min-width: 0;
      `,
    ];
  }

  return innerContainerStyle;
}

const fileNameStyle = css`
  margin: 0;
`;

interface FileNameStyleConfig {
  truncate: boolean;
}

export function getFileNameStyle({ truncate }: FileNameStyleConfig) {
  if (truncate) {
    return [
      fileNameStyle,
      css`
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      `,
    ];
  }

  return [
    fileNameStyle,
    css`
      max-width: 100%;
    `,
  ];
}

export const errorMessagesStyle = css`
  color: ${theme.colors.text.critical} !important;
  margin: 0;
  font-size: ${theme.fontSize(12)} !important;
`;

const eSignContainerStyle = css`
  display: flex;
  justify-content: space-between;
`;

interface ESignContainerStyleConfig {
  isEnhancedFlowEnabled: boolean;
}

export function getESignContainerStyle({ isEnhancedFlowEnabled }: ESignContainerStyleConfig) {
  if (isEnhancedFlowEnabled) {
    return [
      eSignContainerStyle,
      css`
        align-items: center;
        justify-content: flex-end;
        gap: var(--fabric-space-gap-xs);
        margin-left: var(--fabric-space-gap-md);

        @media screen and (max-width: ${breakpoints.small.max}px) {
          justify-content: space-between;
          margin-left: 0;
          width: 100%;

          label {
            font-size: var(--fabric-font-size-sm);
          }
        }
      `,
    ];
  }

  return eSignContainerStyle;
}

export const removeActionContainerStyle = css`
  display: flex;
  align-items: center;
  padding-left: var(--fabric-space-gap-xs);
  border-left: 1px solid var(--fabric-color-neutral-20);

  @media screen and (max-width: ${breakpoints.small.max}px) {
    border-left: 0;
    padding-left: 0;
  }
`;
